/**
 * Modal Component
 * Accessible modal dialog with backdrop
 */

import { HTMLAttributes, forwardRef, useEffect } from 'react'
import { clsx } from 'clsx'
import { X } from 'lucide-react'

export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  open: boolean
  onClose: () => void
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  closeOnBackdrop?: boolean
  showCloseButton?: boolean
}

const sizeStyles = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-full',
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      open,
      onClose,
      size = 'md',
      closeOnBackdrop = true,
      showCloseButton = true,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && open) {
          onClose()
        }
      }

      if (open) {
        document.addEventListener('keydown', handleEscape)
        document.body.style.overflow = 'hidden'
      }

      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.body.style.overflow = 'unset'
      }
    }, [open, onClose])

    if (!open) return null

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
        onClick={closeOnBackdrop ? onClose : undefined}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-50" />

        {/* Modal Content */}
        <div
          ref={ref}
          className={clsx(
            'relative bg-white rounded-lg shadow-xl w-full animate-slide-in',
            'max-h-[90vh] overflow-y-auto',
            sizeStyles[size],
            className,
          )}
          onClick={e => e.stopPropagation()}
          {...props}
        >
          {showCloseButton && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          {children}
        </div>
      </div>
    )
  },
)

Modal.displayName = 'Modal'

export const ModalHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={clsx('px-4 md:px-6 py-3 md:py-4 border-b border-gray-200', className)} {...props}>
        {children}
      </div>
    )
  },
)

ModalHeader.displayName = 'ModalHeader'

export const ModalTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <h2 ref={ref} className={clsx('text-lg md:text-xl font-semibold text-gray-900 pr-8', className)} {...props}>
        {children}
      </h2>
    )
  },
)

ModalTitle.displayName = 'ModalTitle'

export const ModalBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={clsx('px-4 md:px-6 py-3 md:py-4', className)} {...props}>
        {children}
      </div>
    )
  },
)

ModalBody.displayName = 'ModalBody'

export const ModalFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'px-4 md:px-6 py-3 md:py-4 border-t border-gray-200',
          'flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    )
  },
)

ModalFooter.displayName = 'ModalFooter'
